import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Crown, Shield, Users, Zap, ChevronRight, Star } from 'lucide-react';

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-gold-400" />
              <span className="font-heading text-xl font-bold text-white">House of Royalty</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <Link to="/pricing" className="text-zinc-400 hover:text-white transition-colors uppercase tracking-widest text-sm">
                Membership
              </Link>
              <a href="#benefits" className="text-zinc-400 hover:text-white transition-colors uppercase tracking-widest text-sm">
                Benefits
              </a>
              {user ? (
                <Link 
                  to="/dashboard" 
                  className="btn-gold text-sm py-2 px-6"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Link>
              ) : (
                <Link 
                  to="/login" 
                  className="btn-gold text-sm py-2 px-6"
                  data-testid="login-nav-btn"
                >
                  Enter
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-gold-400 uppercase tracking-[0.3em] text-sm mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Exclusive Telegram Community
            </p>
            <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight leading-none text-white mb-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join the Inner Circle of{' '}
              <span className="gold-text">Royalty</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-400 leading-relaxed mb-12 max-w-xl animate-fade-in" style={{ animationDelay: '0.3s' }}>
              Gain exclusive access to our private Telegram community. Connect with like-minded individuals, 
              receive premium insights, and elevate your journey to success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <Link 
                to="/pricing" 
                className="btn-gold inline-flex items-center justify-center gap-2"
                data-testid="get-started-btn"
              >
                Claim Your Throne
                <ChevronRight className="w-5 h-5" />
              </Link>
              <a href="#benefits" className="btn-outline inline-flex items-center justify-center gap-2">
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Banner */}
      <section className="py-12 bg-[#050505] border-y border-white/5" data-testid="partner-banner-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center">
          <p className="text-gold-400 uppercase tracking-[0.3em] text-xs mb-6">Featured Partner</p>
          <a
            href="https://www.kasidie.com/Login.do?method=entry&referredby=HouseofRoyalty"
            data-testid="kasidie-banner-link"
            onClick={() => {
              try {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/banner-click`;
                const body = JSON.stringify({ banner: 'kasidie' });
                if (navigator.sendBeacon) {
                  navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
                } else {
                  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
                }
              } catch (e) { /* never block navigation */ }
            }}
          >
            <img
              src="https://www.kasidie.com/static/graphics/ads/ad-sweetsent-336x280.jpg"
              width="336"
              height="280"
              border="0"
              alt="Kasidie.com - No Illusion! Real People! Real Sex!"
            />
          </a>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="texture-bg py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-gold-400 uppercase tracking-[0.3em] text-sm mb-4">Why Join Us</p>
            <h2 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight text-white">
              Benefits of <span className="gold-text">Membership</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {[
              {
                icon: Shield,
                title: 'Exclusive Access',
                description: 'Enter our private Telegram group reserved only for members'
              },
              {
                icon: Users,
                title: 'Elite Network',
                description: 'Connect with successful individuals on the same path'
              },
              {
                icon: Zap,
                title: 'Premium Insights',
                description: 'Receive actionable strategies and insider knowledge'
              },
              {
                icon: Star,
                title: 'VIP Support',
                description: 'Get priority access to founders and mentors'
              }
            ].map((benefit, index) => (
              <div 
                key={index}
                className="tier-card group animate-fade-in"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
              >
                <benefit.icon className="w-10 h-10 text-gold-400 mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-heading font-medium text-white mb-3">{benefit.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              { value: '500+', label: 'Active Members' },
              { value: '$2M+', label: 'Combined Value Generated' },
              { value: '24/7', label: 'Community Support' }
            ].map((stat, index) => (
              <div key={index} className="animate-fade-in" style={{ animationDelay: `${0.1 * (index + 1)}s` }}>
                <p className="text-5xl md:text-6xl font-heading font-bold gold-text mb-2">{stat.value}</p>
                <p className="text-zinc-500 uppercase tracking-widest text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 texture-bg">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Crown className="w-16 h-16 text-gold-400 mx-auto mb-8" />
          <h2 className="text-3xl md:text-5xl font-heading font-semibold tracking-tight text-white mb-6">
            Ready to Join the <span className="gold-text">Elite</span>?
          </h2>
          <p className="text-zinc-400 mb-12 max-w-xl mx-auto">
            Your throne awaits. Choose your membership tier and unlock instant access to our exclusive Telegram community.
          </p>
          <Link 
            to="/pricing" 
            className="btn-gold inline-flex items-center gap-2"
            data-testid="cta-btn"
          >
            View Membership Tiers
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-gold-400" />
              <span className="font-heading text-lg text-white">House of Royalty</span>
            </div>
            <p className="text-zinc-600 text-sm">
              &copy; {new Date().getFullYear()} House of Royalty. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
